import { useState } from "react";
import { Vault } from "lucide-react";
import * as magazzinoApi from "../../api/magazzino";
import { OrdineMagazzino } from "../../api/magazzino";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { etichettaMatricola } from "../../utils/matricola";

/**
 * La matricola di un ordine è diventata nota.
 *
 * ## Perché è un'azione a sé e non un campo del form
 *
 * Il momento non è fisso: se il fornitore comunica la matricola alla conferma
 * dell'ordine si registra subito, altrimenti si aspetta l'installazione. Un
 * campo dentro il form di creazione avrebbe suggerito che si sa sempre subito,
 * e sarebbe rimasto vuoto quasi sempre.
 *
 * ## Che cosa succede davvero
 *
 * Non si scrive una matricola sull'ordine — su `magazzino_device` quella
 * colonna non esiste. Il backend normalizza il valore (`normalizzaMatricola`,
 * la stessa regola d'identità usata dall'import e dalla creazione device),
 * cerca il pezzo in `devices` e **lo crea se non c'è**, poi apre una riga in
 * `magazzino_device_unita`. Da quel momento l'identità vive lì, dove c'è il
 * vincolo UNIQUE che la garantisce — e una correzione successiva si fa sulla
 * scheda del dispositivo, non da qui.
 *
 * Un ordine ne accetta fino a `quantita`: un ordine da 5 casseforti arriva con
 * 5 matricole, comunicate in momenti diversi. Il pannello dice a che punto si
 * è, così chi registra il terzo pezzo su cinque non deve andarlo a contare.
 */
export function AssegnaMatricola({
  ordine,
  onChiudi,
  onFatto,
}: {
  ordine: OrdineMagazzino;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const { showToast } = useToast();
  const [matricola, setMatricola] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function conferma(e: React.FormEvent) {
    e.preventDefault();
    if (!matricola.trim()) {
      setErrore("La matricola è obbligatoria.");
      return;
    }

    setErrore(null);
    setInCorso(true);
    try {
      const { device, creato, collegate, quantita } = await magazzinoApi.assegnaMatricola(ordine.id, matricola);
      // Dire se il pezzo è nato adesso o esisteva già non è un dettaglio: se
      // esisteva, chi guarda deve sapere che l'ordine si è agganciato a una
      // cassaforte con una storia sua, non a un pezzo nuovo.
      // Su un ordine multiplo il conteggio è la parte utile: dice se ne
      // mancano ancora, che è la domanda successiva di chi ha appena
      // registrato un arrivo.
      const avanzamento = quantita > 1 ? ` — ${collegate} di ${quantita}.` : ".";
      showToast(
        creato
          ? `Dispositivo ${device.matricola} creato e collegato all'ordine${avanzamento}`
          : `Ordine collegato al dispositivo ${device.matricola}, che era già censito${avanzamento}`,
        "success"
      );
      onFatto();
      onChiudi();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile assegnare la matricola."));
    } finally {
      setInCorso(false);
    }
  }

  const modello = ordine.dispositivo_catalogo
    ? `${ordine.dispositivo_catalogo.marca} ${ordine.dispositivo_catalogo.modello}`
    : null;

  return (
    <Modal
      open
      onClose={onChiudi}
      title="Assegna matricola"
      size="sm"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="submit" form="form-assegna-matricola" loading={inCorso}>
            <Vault className="h-4 w-4" aria-hidden="true" />
            Assegna
          </Button>
        </div>
      }
    >
      <form id="form-assegna-matricola" onSubmit={conferma} className="flex flex-col gap-4" noValidate>
        <p className="text-sm text-ink/70">
          Ordine {ordine.odoo_order_ref ? <span className="font-semibold text-ink">{ordine.odoo_order_ref}</span> : "senza riferimento"}
          {modello && <> — {modello}</>}.
        </p>

        {ordine.quantita > 1 && (
          <p className="text-sm text-ink/70">
            <span className="font-semibold text-ink">
              {ordine.unita.length} di {ordine.quantita}
            </span>{" "}
            già arrivate
            {ordine.unita.length > 0 && <> ({ordine.unita.map((u) => etichettaMatricola(u.device.matricola)).join(", ")})</>}.
          </p>
        )}

        <Input
          label="Matricola"
          value={matricola}
          onChange={(e) => {
            setMatricola(e.target.value);
            setErrore(null);
          }}
          error={errore ?? undefined}
          hint={errore ? undefined : "Viene normalizzata (spazi ai lati tolti, maiuscolo) come ovunque nel parco."}
          autoFocus
        />

        <div className="rounded-lg border border-line bg-line/10 p-3 text-xs leading-relaxed text-ink/60">
          <p>
            Se un dispositivo con questa matricola esiste già, l&apos;ordine ci si collega. Altrimenti viene creato
            adesso, col modello dell&apos;ordine.
          </p>
          <p className="mt-2">
            Da qui in poi la matricola vive sulla scheda del dispositivo: eventuali correzioni si fanno lì, non
            sull&apos;ordine.
          </p>
        </div>
      </form>
    </Modal>
  );
}
